<template>
  <div class="tw:flex tw:flex-col tw:gap-4" data-testid="reg-company-brand">
    <span class="tw:flex tw:flex-col tw:gap-0.5">
      <span class="tw:text-13 tw:font-semibold tw:text-gray-900">{{ $t('registration.company.section_brand') }}</span>
      <span class="tw:text-13 tw:text-muted">{{ $t('registration.company.section_brand_hint') }}</span>
    </span>

    <div class="tw:grid tw:grid-cols-1 tw:items-start tw:gap-6 tw:sm:grid-cols-[220px_1fr]">
      <LogoUploadField v-model:logo-id="draft.logoId" :existing-logo-url="existingLogoUrl" />

      <div class="tw:flex tw:flex-col tw:gap-2" data-testid="reg-brand-colours">
        <span class="tw:text-13 tw:font-medium tw:text-gray-700">{{ $t('registration.company.colours') }}</span>

        <div class="tw:flex tw:flex-wrap tw:gap-2.5">
          <ColorChip :label="$t('registration.company.colour_primary')" v-model="primaryColor" />
          <ColorChip :label="$t('registration.company.colour_accent')" v-model="accentColor" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/** The company step's brand section: logo upload and brand colours. */
import { computed } from 'vue';

import LogoUploadField from '@/components/registration/subcomponents/LogoUploadField.vue';
import ColorChip from '@/components/registration/subcomponents/ColorChip.vue';
import type { RegistrationDraft } from '@/types/registration';

/* ----------------------------------- STATE ---------------------------------- */
const draft = defineModel<RegistrationDraft['company']>('draft', { required: true });

/** thumbnail of a logo hydrated from a previous visit; arrives after mount */
defineProps<{ existingLogoUrl?: string | null }>();

/* --------------------------------- COMPUTED --------------------------------- */
const primaryColor = computed({
  get: () => draft.value.brandColors.find((color) => color.role === 'primary')?.value ?? '',
  set: (value: string) => upsertColor('primary', value),
});

const accentColor = computed({
  get: () => draft.value.brandColors.find((color) => color.role === 'accent')?.value ?? '',
  set: (value: string) => upsertColor('accent', value),
});

/* --------------------------------- METHODS ---------------------------------- */
function upsertColor(role: 'primary' | 'accent', value: string) {
  const colors = draft.value.brandColors;
  const existing = colors.find((color) => color.role === role);

  if (!value) {
    if (existing) colors.splice(colors.indexOf(existing), 1);
    return;
  }

  if (existing) {
    existing.value = value;
  } else {
    colors.push({ value, role });
  }
}
</script>
